import type { AdminKpisPdfTrendLineProps } from "../../models/admin-kpis.models";
import {
  ADMIN_KPIS_PRINT_TREND_CHART_WIDTH,
  ADMIN_KPIS_TREND_CHART_HEIGHT,
  ADMIN_KPIS_TREND_PLOT_BOTTOM,
  ADMIN_KPIS_TREND_PLOT_LEFT,
  ADMIN_KPIS_TREND_PLOT_RIGHT,
  ADMIN_KPIS_TREND_PLOT_TOP,
  ADMIN_KPIS_TREND_WINDOW_OPTIONS,
  buildAdminKpisTrendChartData,
  formatAdminKpisTrendDate,
  getAdminKpisTrendColorClassName,
  getAdminKpisTrendDateLabelClassName,
  getAdminKpisTrendPoints,
  getAdminKpisTrendSegment,
} from "../../utils/admin-kpis.utils";
import "./AdminKpisPie.css";
import { memo, useMemo } from "react";
import { twMerge } from "tailwind-merge";

const AdminKpisPdfTrendLine = ({
  activeFilter,
  history,
  periodFleetCertificateStatus,
  periodFleetBalticQuestionnaireStatus,
  selectedWindow,
}: AdminKpisPdfTrendLineProps) => {
  const selectedSegment = useMemo(
    () => getAdminKpisTrendSegment(activeFilter, periodFleetCertificateStatus, periodFleetBalticQuestionnaireStatus),
    [activeFilter, periodFleetBalticQuestionnaireStatus, periodFleetCertificateStatus],
  );
  const trendPoints = useMemo(
    () => getAdminKpisTrendPoints(history, activeFilter, selectedWindow),
    [activeFilter, history, selectedWindow],
  );
  const chartData = useMemo(
    () => buildAdminKpisTrendChartData(trendPoints, ADMIN_KPIS_PRINT_TREND_CHART_WIDTH, selectedWindow),
    [selectedWindow, trendPoints],
  );

  if (!activeFilter) {
    return null;
  }

  const selectedMetricLabel = selectedSegment?.label ?? activeFilter.code;
  const trendColorClassName = getAdminKpisTrendColorClassName(selectedSegment?.code ?? activeFilter.code);
  const selectedWindowLabel =
    ADMIN_KPIS_TREND_WINDOW_OPTIONS.find((option) => option.value === selectedWindow)?.label ?? "30 days";
  const gridLineStart = ADMIN_KPIS_TREND_PLOT_LEFT;
  const gridLineEnd = chartData.chartWidth - ADMIN_KPIS_TREND_PLOT_RIGHT;
  const plotBottom = ADMIN_KPIS_TREND_CHART_HEIGHT - ADMIN_KPIS_TREND_PLOT_BOTTOM;
  const plotMiddle = ADMIN_KPIS_TREND_PLOT_TOP + (plotBottom - ADMIN_KPIS_TREND_PLOT_TOP) / 2;

  return (
    <section className={twMerge("admin-kpis-print-trend", trendColorClassName)}>
      <div className="admin-kpis-print-trend-header">
        <div>
          <h2>Historical Trendline</h2>
          <div className="admin-kpis-print-trend-subtitle">
            <span className="admin-kpis-print-trend-swatch" aria-hidden="true" />
            <span>{selectedMetricLabel}</span>
          </div>
        </div>
        <div className="admin-kpis-print-trend-summary">
          <div>{selectedWindowLabel}</div>
        </div>
      </div>

      {chartData.points.length > 0 ? (
        <svg
          className="admin-kpis-print-trend-chart"
          viewBox={`0 0 ${chartData.chartWidth} ${ADMIN_KPIS_TREND_CHART_HEIGHT}`}
          role="img"
          aria-label={`${selectedMetricLabel} trendline for ${selectedWindowLabel}`}
        >
          <line
            className="admin-kpis-trend-grid-line"
            x1={gridLineStart}
            x2={gridLineEnd}
            y1={ADMIN_KPIS_TREND_PLOT_TOP}
            y2={ADMIN_KPIS_TREND_PLOT_TOP}
          />
          <line
            className="admin-kpis-trend-grid-line"
            x1={gridLineStart}
            x2={gridLineEnd}
            y1={plotMiddle}
            y2={plotMiddle}
          />
          <line
            className="admin-kpis-trend-axis-line"
            x1={gridLineStart}
            x2={gridLineEnd}
            y1={plotBottom}
            y2={plotBottom}
          />
          <text className="admin-kpis-trend-axis-label" x={8} y={ADMIN_KPIS_TREND_PLOT_TOP + 4}>
            {chartData.maxCount}
          </text>
          <text className="admin-kpis-trend-axis-label" x={8} y={plotMiddle + 4}>
            {chartData.midpointCount}
          </text>
          <text className="admin-kpis-trend-axis-label" x={8} y={plotBottom + 4}>
            0
          </text>
          <path className="admin-kpis-trend-line" d={chartData.pathData} />
          {chartData.tooltipPoints.map((point) => (
            <circle key={point.snapshotDate} className="admin-kpis-trend-point" cx={point.x} cy={point.y} r={4} />
          ))}
          {chartData.dateAxisPoints.map((point, index) => (
            <text
              key={point.snapshotDate}
              className={getAdminKpisTrendDateLabelClassName(index, chartData.dateAxisPoints.length)}
              x={point.x}
              y={ADMIN_KPIS_TREND_CHART_HEIGHT - 12}
            >
              {formatAdminKpisTrendDate(point.snapshotDate)}
            </text>
          ))}
        </svg>
      ) : (
        <div className="admin-kpis-print-trend-empty">
          No daily snapshot history is available for this metric in the selected period.
        </div>
      )}
    </section>
  );
};

export default memo(AdminKpisPdfTrendLine);
